import { useEffect, useState } from "react";
import { Loader2, Plus, Save, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useShop } from "@/context/ShopContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GROUPS = [
  {
    id: "images",
    title: "Homepage — Photos",
    fields: [
      { k: "hero_image", l: "Hero banner photo (category tile photos are edited in the Categories tab)", image: true },
    ],
  },
  {
    id: "contact",
    title: "Contact & social links",
    fields: [
      { k: "contact_whatsapp", l: "WhatsApp number (10-digit or with country code)" },
      { k: "contact_email", l: "Support email" },
      { k: "contact_instagram", l: "Instagram profile URL" },
    ],
  },
  {
    id: "hero",
    title: "Homepage — Hero banner text",
    fields: [
      { k: "hero_eyebrow", l: "Small top label" },
      { k: "hero_line1", l: "Headline — line 1" },
      { k: "hero_line2a", l: "Headline — line 2 (first part)" },
      { k: "hero_line2b", l: "Headline — line 2 (highlighted words)" },
      { k: "hero_subtext", l: "Intro paragraph", multi: true },
      { k: "hero_cta_primary", l: "Main button text" },
      { k: "hero_cta_secondary", l: "Secondary button text" },
      { k: "hero_badge1", l: "Floating badge on photo (top)" },
      { k: "hero_badge2", l: "Floating badge on photo (offer)" },
      { k: "hero_chip1", l: "Trust chip 1" },
      { k: "hero_chip2", l: "Trust chip 2" },
      { k: "hero_chip3", l: "Trust chip 3" },
    ],
  },
  {
    id: "sections",
    title: "Homepage — Ticker & section titles",
    fields: [
      { k: "marquee_items", l: "Scrolling ticker items (one per line)", multi: true },
      { k: "categories_eyebrow", l: "Categories — small label" },
      { k: "categories_title", l: "Categories — heading" },
      { k: "bestsellers_eyebrow", l: "Bestsellers — small label" },
      { k: "bestsellers_title", l: "Bestsellers — heading" },
      { k: "shelf_eyebrow", l: "Full collection — small label" },
      { k: "shelf_title", l: "Full collection — heading" },
    ],
  },
  {
    id: "cta",
    title: "Homepage — Bottom banner",
    fields: [
      { k: "cta_eyebrow", l: "Small label" },
      { k: "cta_title", l: "Heading", multi: true },
      { k: "cta_button", l: "Button text" },
    ],
  },
  {
    id: "values",
    title: "Homepage — Trust strip (4 items)",
    fields: [1, 2, 3, 4].flatMap((i) => [
      { k: `value${i}_title`, l: `Item ${i} — title` },
      { k: `value${i}_desc`, l: `Item ${i} — description` },
    ]),
  },
  {
    id: "footer",
    title: "Footer",
    fields: [
      { k: "footer_blurb", l: "Brand description", multi: true },
      { k: "footer_know1", l: "Good to know — line 1" },
      { k: "footer_know2", l: "Good to know — line 2" },
      { k: "footer_know3", l: "Good to know — line 3" },
      { k: "footer_know4", l: "Good to know — line 4" },
    ],
  },
  {
    id: "announcement",
    title: "Announcement bar (festive / sale banner)",
    fields: [
      { k: "announcement_enabled", l: "Show announcement bar", toggle: true },
      { k: "announcement_text", l: "Banner text" },
      { k: "announcement_link", l: "Button link (e.g. /shop or /shop?cat=Mugs)" },
      { k: "announcement_link_label", l: "Button label" },
    ],
  },
  {
    id: "shipping",
    title: "Shipping & free gift",
    fields: [
      { k: "shipping_free_threshold", l: "Free shipping above ₹" },
      { k: "shipping_standard_fee", l: "Standard delivery charge ₹" },
      { k: "shipping_priority_fee", l: "Priority delivery charge ₹" },
      { k: "freebie_enabled", l: "Free gift offer", toggle: true },
      { k: "freebie_threshold", l: "Free gift unlocked above cart value ₹" },
      { k: "freebie_product_id", l: "Gift product", product: true },
      { k: "freebie_label", l: "Gift label shown to shoppers" },
    ],
  },
  {
    id: "checkout",
    title: "Checkout & payment",
    fields: [
      { k: "cod_enabled", l: "Cash on Delivery (COD) option at checkout", toggle: true },
      { k: "cod_note", l: "COD note shown to shoppers", multi: true },
      { k: "checkout_payment_note", l: "Payment note shown if online payment is unavailable", multi: true },
    ],
  },
  {
    id: "wholesale",
    title: "Wholesale page",
    fields: [
      { k: "wholesale_eyebrow", l: "Small top label" },
      { k: "wholesale_title", l: "Page heading" },
      { k: "wholesale_intro", l: "Intro paragraph", multi: true },
    ],
  },
];

const SECTION_PAGES = [
  { prefix: "terms", title: "Terms & Conditions page", introKey: "terms_intro", sectionsKey: "terms_sections" },
  { prefix: "returns", title: "Returns & Exchange page", introKey: "returns_intro", sectionsKey: "returns_sections" },
];

const inputCls = "h-11 w-full rounded-lg border border-linen bg-white px-3.5 text-sm outline-none transition-colors focus:border-terra";
const areaCls = "w-full rounded-lg border border-linen bg-white px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-terra";

const ContentTab = () => {
  const { products } = useShop();
  const [values, setValues] = useState(null);
  const [saving, setSaving] = useState("");
  const [uploading, setUploading] = useState("");

  useEffect(() => {
    api.get("/content").then((r) => setValues(r.data)).catch(() => toast.error("Could not load content"));
  }, []);

  const setVal = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const uploadImage = async (k, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(k);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setVal(k, `${BACKEND_URL}${r.data.url}`);
      toast.success("Photo uploaded — press Save to publish it");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed — try again");
    } finally {
      setUploading("");
      e.target.value = "";
    }
  };

  const save = async (groupId, keys) => {
    setSaving(groupId);
    try {
      const payload = {};
      keys.forEach((k) => (payload[k] = values[k]));
      await api.put("/content", { values: payload });
      toast.success("Saved — live on the website now");
    } catch {
      toast.error("Save failed — try again");
    } finally {
      setSaving("");
    }
  };

  if (!values) return <div className="h-64 animate-pulse rounded-2xl bg-sand" />;

  const renderField = (f) => {
    if (f.toggle) {
      return (
        <div key={f.k} className="flex items-center justify-between rounded-lg border border-linen bg-white px-3.5 py-2.5">
          <label className="text-xs font-semibold text-clay">{f.l}</label>
          <Switch checked={values[f.k] === "true"} onCheckedChange={(v) => setVal(f.k, v ? "true" : "false")} data-testid={`content-field-${f.k}`} />
        </div>
      );
    }
    if (f.product) {
      return (
        <div key={f.k}>
          <label className="mb-1.5 block text-xs font-semibold text-clay">{f.l}</label>
          <Select value={values[f.k] || ""} onValueChange={(v) => setVal(f.k, v)}>
            <SelectTrigger className="h-11 border-linen bg-white" data-testid={`content-field-${f.k}`}><SelectValue placeholder="Choose a product" /></SelectTrigger>
            <SelectContent>
              {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      );
    }
    if (f.image) {
      return (
        <div key={f.k} className="sm:col-span-2">
          <label className="mb-1.5 block text-xs font-semibold text-clay">{f.l}</label>
          <div className="flex items-start gap-4">
            <img src={values[f.k]} alt={f.l} className="h-28 w-28 shrink-0 rounded-xl border border-linen object-cover" />
            <div className="flex-1 space-y-2">
              <label
                className={`inline-flex cursor-pointer items-center gap-2 rounded-lg bg-clay px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-cream transition-colors hover:bg-terra ${uploading === f.k ? "pointer-events-none opacity-60" : ""}`}
                data-testid={`content-upload-${f.k}`}
              >
                {uploading === f.k ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                {uploading === f.k ? "Uploading…" : "Upload new photo"}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(f.k, e)} />
              </label>
              <input value={values[f.k] ?? ""} onChange={(e) => setVal(f.k, e.target.value)} className={inputCls} placeholder="…or paste an image URL" data-testid={`content-field-${f.k}`} />
            </div>
          </div>
        </div>
      );
    }
    return (
      <div key={f.k} className={f.multi ? "sm:col-span-2" : ""}>
        <label className="mb-1.5 block text-xs font-semibold text-clay">{f.l}</label>
        {f.multi ? (
          <textarea rows={3} value={values[f.k] ?? ""} onChange={(e) => setVal(f.k, e.target.value)} className={areaCls} data-testid={`content-field-${f.k}`} />
        ) : (
          <input value={values[f.k] ?? ""} onChange={(e) => setVal(f.k, e.target.value)} className={inputCls} data-testid={`content-field-${f.k}`} />
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="admin-content-tab">
      <p className="rounded-xl border border-ochre/40 bg-ochre/10 px-4 py-3 text-sm text-clay">
        Everything you edit here goes live on the website the moment you save that section. Visitors see the new text immediately.
      </p>

      {GROUPS.map((g) => (
        <div key={g.id} className="rounded-2xl border border-linen bg-white p-6">
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-lg text-espresso">{g.title}</h3>
            <button
              onClick={() => save(g.id, g.fields.map((f) => f.k))}
              disabled={saving === g.id}
              data-testid={`content-save-${g.id}`}
              className="flex items-center gap-1.5 rounded-full bg-espresso px-4 py-2 text-xs font-semibold text-cream transition-colors hover:bg-terra disabled:opacity-50"
            >
              {saving === g.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
            </button>
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">{g.fields.map(renderField)}</div>
        </div>
      ))}

      {SECTION_PAGES.map((p) => {
        const sections = values[p.sectionsKey] || [];
        const updateSection = (i, field, val) =>
          setVal(p.sectionsKey, sections.map((s, x) => (x === i ? { ...s, [field]: val } : s)));
        return (
          <div key={p.prefix} className="rounded-2xl border border-linen bg-white p-6" data-testid={`content-${p.prefix}-editor`}>
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-lg text-espresso">{p.title}</h3>
              <button
                onClick={() => save(p.prefix, [p.introKey, p.sectionsKey])}
                disabled={saving === p.prefix}
                data-testid={`content-save-${p.prefix}`}
                className="flex items-center gap-1.5 rounded-full bg-espresso px-4 py-2 text-xs font-semibold text-cream transition-colors hover:bg-terra disabled:opacity-50"
              >
                {saving === p.prefix ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
              </button>
            </div>
            <div className="mt-4">
              <label className="mb-1.5 block text-xs font-semibold text-clay">Intro paragraph</label>
              <textarea rows={2} value={values[p.introKey] ?? ""} onChange={(e) => setVal(p.introKey, e.target.value)} className={areaCls} />
            </div>
            <div className="mt-5 space-y-4">
              {sections.map((s, i) => (
                <div key={i} className="rounded-xl border border-linen bg-cream/50 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <input value={s.title} onChange={(e) => updateSection(i, "title", e.target.value)} className={inputCls} placeholder="Section title" />
                    <button
                      onClick={() => setVal(p.sectionsKey, sections.filter((_, x) => x !== i))}
                      aria-label="Remove section"
                      className="shrink-0 rounded-lg p-2 text-clay/50 transition-colors hover:bg-destructive/10 hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <textarea rows={3} value={s.body} onChange={(e) => updateSection(i, "body", e.target.value)} className={`${areaCls} mt-3`} placeholder="Section text" />
                </div>
              ))}
              <button
                onClick={() => setVal(p.sectionsKey, [...sections, { title: "", body: "" }])}
                data-testid={`content-${p.prefix}-add-section`}
                className="flex items-center gap-1.5 rounded-full border border-dashed border-clay/40 px-4 py-2 text-xs font-semibold text-clay transition-colors hover:border-terra hover:text-terra"
              >
                <Plus className="h-3.5 w-3.5" /> Add section
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ContentTab;
